/**
 * BootScene — 에셋 로딩
 * 이미지 파일 및 스프라이트 시트를 로드하고, 애니메이션을 생성합니다.
 */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, CloudType } from '../config';

/* 스프라이트 시트 프레임 크기 (640÷4 = 160, 640÷3 ≈ 213) */
const SPRITE_FRAME_W = 160;
const SPRITE_FRAME_H = 213;

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // ── 로딩 프로그레스 바 ──
    const barW = GAME_WIDTH * 0.6;
    const barH = 12;
    const barX = (GAME_WIDTH - barW) / 2;
    const barY = GAME_HEIGHT / 2;

    this.add.text(GAME_WIDTH / 2, barY - 30, 'Loading...', {
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '16px',
      color: '#FFFFFF',
    }).setOrigin(0.5);

    const bgBar = this.add.rectangle(GAME_WIDTH / 2, barY, barW + 4, barH + 4, 0x222244, 0.8).setOrigin(0.5);
    const bar = this.add.rectangle(barX + 2, barY, 0, barH, 0xBB88FF).setOrigin(0, 0.5);

    this.load.on('progress', (value: number) => {
      bar.width = barW * value;
    });

    // ── 배경 이미지 ──
    this.load.image('bg_day', '/assets/backgrounds/bg_day.png');
    this.load.image('bg_sunset', '/assets/backgrounds/bg_sunset.png');
    this.load.image('bg_night', '/assets/backgrounds/bg_night.png');
    this.load.image('bg_space', '/assets/backgrounds/bg_space.png');

    // ── 캐릭터 스프라이트 시트 ──
    this.load.spritesheet('player_idle', '/assets/sprites/player_idle.png', {
      frameWidth: SPRITE_FRAME_W,
      frameHeight: SPRITE_FRAME_H,
    });
    this.load.spritesheet('player_jump', '/assets/sprites/player_jump.png', {
      frameWidth: SPRITE_FRAME_W,
      frameHeight: SPRITE_FRAME_H,
    });

    // 단일 이미지 (메뉴 미리보기용)
    this.load.image('player', '/assets/sprites/player.png');

    // ── 구름 ──
    this.load.image(`cloud_${CloudType.NORMAL}`, '/assets/sprites/cloud_normal.png');
    this.load.image(`cloud_${CloudType.RAINBOW}`, '/assets/sprites/cloud_rainbow.png');
    this.load.image(`cloud_${CloudType.LIGHTNING}`, '/assets/sprites/cloud_lightning.png');
    this.load.image(`cloud_${CloudType.STAR}`, '/assets/sprites/cloud_star.png');
    this.load.image(`cloud_${CloudType.WIND}`, '/assets/sprites/cloud_wind.png');
  }

  create(): void {
    // ── 런타임 생성 텍스처 (파티클용) ──
    this.generateParticleTextures();

    // ── 스프라이트 애니메이션 생성 ──
    this.createAnimations();

    this.scene.start('MenuScene');
  }

  private createAnimations(): void {
    // Idle 애니메이션 (12 프레임, 느린 속도)
    this.anims.create({
      key: 'player_idle_anim',
      frames: this.anims.generateFrameNumbers('player_idle', { start: 0, end: 11 }),
      frameRate: 6,
      repeat: -1,
    });

    // Jump 애니메이션 (12 프레임)
    this.anims.create({
      key: 'player_jump_anim',
      frames: this.anims.generateFrameNumbers('player_jump', { start: 0, end: 11 }),
      frameRate: 14,
      repeat: 0,
    });

    // Jump loop (공중 체류 — jump 시트의 중간 프레임들)
    this.anims.create({
      key: 'player_float_anim',
      frames: this.anims.generateFrameNumbers('player_jump', { start: 3, end: 7 }),
      frameRate: 8,
      repeat: -1,
    });
  }

  private generateParticleTextures(): void {
    // 파티클
    const g = this.make.graphics({ x: 0, y: 0 });
    g.fillStyle(0xFFFFFF);
    g.fillCircle(4, 4, 4);
    g.generateTexture('particle', 8, 8);
    g.destroy();

    // 작은 파티클
    const g2 = this.make.graphics({ x: 0, y: 0 });
    g2.fillStyle(0xFFFFFF);
    g2.fillCircle(2, 2, 2);
    g2.generateTexture('particle_small', 4, 4);
    g2.destroy();

    // 별 (배경용)
    const g3 = this.make.graphics({ x: 0, y: 0 });
    g3.fillStyle(0xFFFFFF, 0.8);
    g3.fillCircle(2, 2, 2);
    g3.generateTexture('star_bg', 4, 4);
    g3.destroy();

    // 코인 이펙트
    const g4 = this.make.graphics({ x: 0, y: 0 });
    g4.fillStyle(0xFFD700);
    g4.fillCircle(6, 6, 6);
    g4.fillStyle(0xFFEE88);
    g4.fillCircle(5, 5, 4);
    g4.generateTexture('coin', 12, 12);
    g4.destroy();
  }
}
